// app/services/invoice.service.js
import { api } from './api';
import { API_BASE_URL } from '../utils/constants';

function filenameFromDisposition(header, fallback) {
    if (!header) return fallback;
    const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(header);
    if (!match) return fallback;
    try {
        return decodeURIComponent(match[1].replace(/["']/g, ''));
    } catch {
        return match[1].replace(/["']/g, '') || fallback;
    }
}

async function fetchPdfDownload(endpoint, fallbackName) {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
        },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({
            detail: `HTTP ${response.status}: ${response.statusText}`,
        }));
        const message = typeof error.detail === 'string' ? error.detail : 'Download failed';
        const apiError = new Error(message);
        apiError.status = response.status;
        throw apiError;
    }
    const blob = await response.blob();
    const fileName = filenameFromDisposition(
        response.headers.get('Content-Disposition'),
        fallbackName,
    );
    return { blob, fileName };
}

function triggerBlobDownload(blob, fileName) {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
}

class InvoiceService {
    // Get invoices with pagination and filters
    async getInvoices(params = {}) {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/api/invoices/?${queryString}`);
    }

    // Get single invoice by ID
    async getInvoiceById(invoiceId) {
        return api.get(`/api/invoices/${invoiceId}`);
    }

    // Generate service invoice
    async generateServiceInvoice(serviceId, applyPolicyDiscount = true) {
        return api.post('/api/invoices/generate/service', {
            service_id: serviceId,
            apply_policy_discount: applyPolicyDiscount
        });
    }

    // Generate miscellaneous invoice
    async generateMiscInvoice(vendorId, amount, description, paymentMethod = null) {
        return api.post('/api/invoices/generate/misc', {
            vendor_id: vendorId,
            amount: amount,
            description: description,
            payment_method: paymentMethod
        });
    }

    // Submit invoice for approval
    async submitInvoice(invoiceId) {
        return api.post(`/api/invoices/${invoiceId}/submit`);
    }

    // Approve invoice
    async approveInvoice(invoiceId, notes = null) {
        const payload = { action: 'approve' };
        if (notes) payload.notes = notes;
        return api.post(`/api/invoices/${invoiceId}/approval`, payload);
    }

    // Reject invoice
    async rejectInvoice(invoiceId, reason, notes = null) {
        const payload = { action: 'reject', reason: reason };
        if (notes) payload.notes = notes;
        return api.post(`/api/invoices/${invoiceId}/approval`, payload);
    }

    // Update payment status
    async updatePaymentStatus(invoiceId, paymentMethod, paymentReference, notes = null) {
        const payload = {
            payment_method: paymentMethod,
            payment_reference: paymentReference
        };
        if (notes) payload.notes = notes;
        return api.post(`/api/invoices/${invoiceId}/payment`, payload);
    }

    // Download invoice
    async downloadInvoice(invoiceId, invoiceNumber) {
        const fallback = invoiceNumber ? `${invoiceNumber}.pdf` : `invoice-${invoiceId}.pdf`;
        const { blob, fileName } = await fetchPdfDownload(
            `/api/invoices/${invoiceId}/download`,
            fallback,
        );
        triggerBlobDownload(blob, fileName);
    }

    // Download vendor invoice
    async downloadVendorInvoice(invoiceId) {
        return api.get(`/api/invoices/${invoiceId}/download/vendor-invoice`);
    }

    // Get vendor invoice summary
    async getVendorInvoiceSummary(vendorId, dateFrom = null, dateTo = null) {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;

        const queryString = new URLSearchParams(params).toString();
        return api.get(`/api/invoices/vendor/${vendorId}/summary?${queryString}`);
    }

    // --- Vendor Invoice (VendorInvoice table) methods ---

    async getVendorInvoices(params = {}) {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/api/invoices/vendor-invoices/?${queryString}`);
    }

    async getVendorInvoiceById(id) {
        return api.get(`/api/invoices/vendor-invoices/${id}`);
    }

    async approveVendorInvoice(id) {
        return api.post(`/api/invoices/vendor-invoices/${id}/approve`);
    }

    async rejectVendorInvoice(id, reason) {
        return api.post(`/api/invoices/vendor-invoices/${id}/reject`, { action: 'reject', reason });
    }

    async downloadVendorInvoicePdf(id) {
        return api.get(`/api/invoices/vendor-invoices/${id}/download`);
    }
}

export const invoiceService = new InvoiceService(); 